import { Badge, Switch } from "@/components/ui";
import { capitalize, cn, status } from "@/lib/utils";
import { routes } from "@/routes/utils";
import {
  useGeoRegionsQuery,
  useToogleUpdateGeoRegionsMutation,
} from "@/services/masterData";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DEFAULT_FILTER } from "./consts";

const useGeographicalRegions = () => {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState(DEFAULT_FILTER);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();

  const queryParams = {
    ...filterData,
    ...(filterData.name ? { name: filterData.name } : {}),
    page: pageNumber + 1, // API expects 1-based page numbers
    orderBy: "createdAt",
    orderDirection: "DESC",
    limit: pageSize,
  };

  const {
    data: response,
    isPending,
    isError,
    error,
  } = useGeoRegionsQuery(queryParams);

  const { mutate: toggleGeoRegion } = useToogleUpdateGeoRegionsMutation({
    onSuccess: () => {
      toast.success("Geo Region Status Updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["geoRegions"] });
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error("Failed to fetch geo regions");
    }
  }, [isError, error]);

  const tableData = response?.data?.[0]?.records || [];
  const totalRecords = response?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const columns = [
    {
      accessorKey: "name",
      header: <div>Name</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={()=>onRowClick(row)}
        >
          {String(row?.row?.original?.name || "")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "description",
      header: <div>Description</div>,
      accessorFn: (row: any) => String(row?.description || ""),
    },
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) =>
        moment(row?.createdAt).format("DD-MM-YYYY") || "",
    },
    {
      accessorKey: "active",
      header: <div>Status</div>,
      cell: (row: any) => {
        const _status = row.row.original.isActive ? "active" : "inactive";
        return (
          <Badge
            variant="outline"
            className={cn(
              (status as any)[_status]?.color,
              (status as any)[_status]?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {capitalize(row?.row?.original?.isActive ? "Active" : "Inactive")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Switch
          checked={row.row.original.isActive}
          className="cursor-pointer"
          onCheckedChange={() => toggleGeoRegion({ id: row.row.original.id })}
        />
      ),
    },
  ];

  const onAdd = () => {
    navigate(`${routes.geoRegion}/${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${routes.geoRegion}/${row?.row?.original.id}/${routes.edit}`);
  };

  const onCancelFilter = () => {
    setOpenFilter(false); // ✅ only closes drawer
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  return {
    columns,
    onAdd,
    onRowClick,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    isPending,
    isError,
    error,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
  };
};

export default useGeographicalRegions;
